/**
 * EnquiriesPage Component
 * Main page for managing enquiries in collins-mobile
 * Replicates functionality from collins-admin with collins-mobile styling patterns
 */

import React, { useCallback, useMemo } from "react";

import { PageContainer } from "../../components/Layout/PageContainer/PageContainer";
import { useAppContext } from "../../hooks/useAppContext";
import { useEnquiriesState } from "./useEnquiriesState";
import { EnquiriesHeader } from "./Header";
import { EnquiriesSidebar, EnquiriesMain } from "./Content";
import { EnquiryStatus, EnquiriesSortOption } from "./types";

export function EnquiriesPage() {
  const {
    enquiries,
    loading,
    error,
    filters,
    sortOption,
    searchQuery,
    facetCounts,
    total,
    offset,
    limit,
    recentlyUpdatedIds,
    updateFilter,
    batchUpdateFilters,
    clearFilters,
    updateSort,
    updateSearch,
    onPaginate,
    takeEnquiry,
    updateStatus,
    refetch
  } = useEnquiriesState();

  const { venueGroupConfig } = useAppContext();
  const users = useMemo(
    () => venueGroupConfig?.users ?? [],
    [venueGroupConfig?.users]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSearch(e.target.value);
    },
    [updateSearch]
  );

  const handleSortChange = useCallback(
    (value: EnquiriesSortOption) => {
      updateSort(value);
    },
    [updateSort]
  );

  const handleStatusFilterClick = useCallback(
    (status: EnquiryStatus) => {
      const currentStatuses = filters.status ?? [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status];
      updateFilter("status", newStatuses.length > 0 ? newStatuses : undefined);
    },
    [filters.status, updateFilter]
  );

  return (
    <PageContainer
      header={{
        content: (
          <EnquiriesHeader
            searchQuery={searchQuery}
            sortOption={sortOption}
            facetCounts={facetCounts}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onRefresh={refetch}
          />
        )
      }}
      sidebar={{
        content: (
          <EnquiriesSidebar
            filters={filters}
            facetCounts={facetCounts}
            users={users}
            updateFilter={updateFilter}
            batchUpdateFilters={batchUpdateFilters}
          />
        )
      }}
      main={{
        content: (
          <EnquiriesMain
            enquiries={enquiries}
            loading={loading}
            error={error}
            filters={filters}
            total={total}
            offset={offset}
            limit={limit}
            recentlyUpdatedIds={recentlyUpdatedIds}
            takeEnquiry={takeEnquiry}
            updateStatus={updateStatus}
            refetch={refetch}
            onPaginate={onPaginate}
            onStatusFilterClick={handleStatusFilterClick}
            onClearFilters={clearFilters}
          />
        )
      }}
    />
  );
}
